// @systeme: navigation-client
// ============================================================================
// PairsForm — Casquettes et sélecteur d'organisation (retour bêta 1, lot D,
// 2026-10-27 — décision FX Q1 : « expliquer + masquer »)
// ============================================================================
// « Dans Mes organisations, j'apparais de deux manières : Cecodev Animateur,
// Participant ET, en dessous, Cecodev_Inter Animateur. Je peux cliquer sur
// l'un ou l'autre, mais je ne vois pas du tout ce que ça change. » — et de
// fait, pour un animateur ou un participant, ça ne change rien : ses espaces
// personnels (Mon espace, Mes séances, Mes inscriptions) réunissent toutes ses
// casquettes, et les pages d'une organisation portent l'org dans leur URL
// (lot 9.1 : la garde de route bascule le jeton toute seule).
//
// Le sélecteur n'a donc de sens que pour qui PILOTE plusieurs organisations
// (ou peut en visiter : super-admin, pilote Cecodev). Règles pures, testées :
//   - une org « utile » = une org où l'on est pilote ou super-admin ;
//   - afficher si ≥ 2 orgs utiles, ou si l'on peut visiter d'autres orgs ;
//   - les lignes listées = les orgs utiles (+ l'org active si elle n'en est
//     pas, pour que la ligne cochée existe toujours) ;
//   - libellés humains des casquettes et des types d'organisation.
// ============================================================================

import type { Membership, OrganisationType, UserRole } from "@cecodev/shared";

export const LIBELLES_CASQUETTE: Record<UserRole, string> = {
  SUPER_ADMIN: "Administrateur plateforme",
  PILOTE: "Pilote",
  ANIMATEUR: "Animateur",
  PARTICIPANT: "Participant",
};

const LIBELLES_TYPE_ORG: Record<OrganisationType, string> = {
  CECODEV: "Cecodev — formation, supervision, événements",
  INTER: "Marketplace Codev",
  STANDARDS: "Standards",
  CLIENT_INTRA: "Organisation cliente",
  OF: "Organisme de formation",
};

/** « Pilote · Animateur » — jamais un code brut à l'écran. */
export function libelleCasquettes(roles: readonly UserRole[]): string {
  const ordre: UserRole[] = ["SUPER_ADMIN", "PILOTE", "ANIMATEUR", "PARTICIPANT"];
  const vus = ordre.filter((r) => roles.includes(r));
  return vus.map((r) => LIBELLES_CASQUETTE[r]).join(" · ");
}

export function libelleTypeOrganisation(type: OrganisationType | string | null | undefined): string {
  return (type && (LIBELLES_TYPE_ORG as Record<string, string>)[type]) || "Organisation";
}

/** Une org où l'on a quelque chose à PILOTER — la seule qui justifie un sélecteur. */
export function estOrgUtile(m: Pick<Membership, "roles">): boolean {
  return m.roles.includes("PILOTE") || m.roles.includes("SUPER_ADMIN");
}

export const EXPLICATION_SELECTEUR =
  "Vous pilotez plusieurs organisations : choisissez celle dont vous voulez gérer les projets, les groupes et les réglages. " +
  "Vos espaces personnels (Mon espace, Mes séances, Mes inscriptions) réunissent toutes vos casquettes et ne changent pas.";

export interface DecisionSelecteurOrg {
  /** Afficher le bloc « Mes organisations » (lignes cliquables). */
  afficherMemberships: boolean;
  /** Afficher le sélecteur dans son ensemble (memberships OU visite). */
  afficher: boolean;
  /** Lignes à lister : orgs utiles, plus l'org active si elle n'en est pas. */
  lignes: Membership[];
}

export function decisionSelecteurOrg(opts: {
  memberships: readonly Membership[];
  activeMembershipId: string | null | undefined;
  peutVisiter: boolean;
}): DecisionSelecteurOrg {
  const utiles = opts.memberships.filter(estOrgUtile);
  const afficherMemberships = utiles.length > 1;
  const active = opts.memberships.find((m) => m.id === opts.activeMembershipId);
  const lignes = afficherMemberships
    ? active && !utiles.some((m) => m.id === active.id) ? [...utiles, active] : [...utiles]
    : [];
  return { afficherMemberships, afficher: afficherMemberships || opts.peutVisiter, lignes };
}
